import type { EChartsOption } from 'echarts';
import { useMemo } from 'react';

import { useTheme } from '../../theme/readTheme';
import type { ChartData, ChartFields } from '../../types';
import { EChart } from '../EChart';
import { Plot } from '../Plot';
import { Empty } from '../states';
import { buildFrame, isEmpty, sum } from '../frame';
import { compact } from '../format';
import { animation } from '../options';

/**
 * One value against a range — the "how far along?" dial.
 *
 * Takes the headline figure (the latest period of a temporal series, or the
 * total otherwise) and draws it on an arc. The max is inferred: a value that
 * looks like a rate (0–1 or 0–100) uses 100; anything else rounds up to a nice
 * ceiling above the value, so the needle sits meaningfully inside the arc
 * rather than pinned at either end.
 */

interface Props {
  data: ChartData;
  /** Author's column choices; absent means infer. */
  fields?: ChartFields;
}

/** Round up to a "nice" ceiling (1/2/5 × 10ⁿ) above v. */
const niceMax = (v: number): number => {
  if (v <= 0) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(v)));
  for (const step of [1, 2, 5, 10]) {
    if (v <= step * mag) return step * mag;
  }
  return 10 * mag;
};

const gaugeView = (data: ChartData, theme: ReturnType<typeof useTheme>, fields?: ChartFields) => {
  const frame = buildFrame(data, fields);
  if (isEmpty(frame)) return null;

  const values = frame.series[0].values.filter((v): v is number => v !== null);
  if (!values.length) return null;

  const periodic = frame.temporal && values.length > 1;
  const value = periodic ? values[values.length - 1] : sum(values);

  // A rate-looking value (fits in 0–100) uses a percentage dial; otherwise a
  // nice numeric ceiling above the value.
  const looksRate = value >= 0 && value <= 100;
  const max = looksRate ? 100 : niceMax(value);
  const label = frame.measureName || frame.series[0].name;
  const brand = theme.brand;

  const option: EChartsOption = {
    ...animation,
    series: [
      {
        type: 'gauge' as const,
        min: 0,
        max,
        startAngle: 210,
        endAngle: -30,
        radius: '92%',
        center: ['50%', '58%'],
        progress: { show: true, width: 14, roundCap: true, itemStyle: { color: brand } },
        axisLine: { lineStyle: { width: 14, color: [[1, theme.surfaceSunken]] } },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false },
        pointer: { show: false },
        anchor: { show: false },
        title: {
          show: true,
          offsetCenter: [0, '38%'],
          color: theme.inkMuted,
          fontSize: 12,
          fontFamily: theme.font,
        },
        detail: {
          valueAnimation: true,
          offsetCenter: [0, '2%'],
          color: theme.ink,
          fontSize: 30,
          fontWeight: 600,
          fontFamily: theme.font,
          formatter: (v: number) => (looksRate ? `${Math.round(v)}` : compact(v)),
        },
        data: [{ value, name: label }],
      },
    ],
  };

  return { option };
};

export const GaugeChart = ({ data, fields }: Props) => {
  const theme = useTheme();
  const view = useMemo(() => gaugeView(data, theme, fields), [data, theme, fields]);
  if (!view) return <Empty message="Needs a single value to gauge" />;
  return (
    <Plot>
      <EChart option={view.option} ariaLabel="Gauge" />
    </Plot>
  );
};
